// Shared Transaction History Component
// Displays DataTrace transaction history with status checking and report retrieval
import React, { useState } from 'react';
import './TransactionHistory.css';

function TransactionHistory({
  transactions = [],
  loading = false,
  pollingTransactions = {},
  onCheckStatus,
  onGetReport,
  showHistory = false,
  onToggleHistory
}) {
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  if (!showHistory && onToggleHistory) {
    return (
      <div className="history-panel">
        <button
          className="history-toggle-btn"
          onClick={onToggleHistory}
        >
          📜 Transaction History ({transactions.length})
          <span className="toggle-icon">▶</span>
        </button>
      </div>
    );
  }

  // Helper to determine if a transaction is from a synchronous product
  const isSyncTransaction = (txn) => {
    // Synchronous products: property_data (includes instant_recordable_legal and property_detail)
    // Asynchronous products: tax, tiqsa, and others
    const syncProducts = ['property_data'];
    return syncProducts.includes(txn.productCode);
  };

  // Helper to determine if a transaction is completed
  const isCompleted = (txn) => {
    const status = (txn.status || '').toLowerCase().trim();
    // Check if status contains 'completed', 'success', 'available', or 'fulfilled'
    // This handles: COMPLETED, COMPLETED_WITH_ERRORS, SUCCESS, AVAILABLE, FULFILLED, etc.
    return status.includes('completed') || status.includes('success') ||
           status.includes('available') || status.includes('fulfilled');
  };

  // Format JSON for display
  const formatJSON = (data) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  return (
    <div className="history-panel">
      {onToggleHistory && (
        <button
          className="history-toggle-btn"
          onClick={onToggleHistory}
        >
          📜 Transaction History ({transactions.length})
          <span className="toggle-icon">▼</span>
        </button>
      )}

      <div className="history-content">
        {loading ? (
          <div className="history-loading">Loading history...</div>
        ) : transactions.length === 0 ? (
          <div className="history-empty">No transaction history yet</div>
        ) : (
          <div className="history-list">
            {transactions.map((txn) => {
              const isSync = isSyncTransaction(txn);
              const hasResponseData = txn.responseData || txn.statusResponse;

              return (
                <div key={txn.transactionId} className="history-item">
                  <div className="history-item-header">
                    <div className="history-item-info">
                      <span className={`history-status-badge ${txn.status || 'pending'}`}>
                        {txn.status || 'Pending'}
                      </span>
                      <span className="history-timestamp">
                        {new Date(txn.timestamp).toLocaleString()}
                      </span>
                      {isSync && <span className="sync-badge" title="Synchronous API call">⚡ Sync</span>}
                    </div>
                    <div className="history-item-actions">
                      {/* For synchronous transactions, show View Request/Response button */}
                      {isSync && hasResponseData && (
                        <button
                          className="history-view-btn"
                          onClick={() => setSelectedTransaction(txn)}
                          title="View request and response payloads"
                        >
                          📄 View Details
                        </button>
                      )}

                      {/* For asynchronous transactions, show Check Status button */}
                      {!isSync && onCheckStatus && (
                        <button
                          className="history-check-btn"
                          onClick={() => onCheckStatus(txn.transactionId)}
                          disabled={pollingTransactions[txn.transactionId] === 'loading'}
                          title="Check transaction status"
                        >
                          {pollingTransactions[txn.transactionId] === 'loading'
                            ? '⏳'
                            : '🔄'}
                        </button>
                      )}

                      {/* Show Get Report button for completed ASYNC transactions only - Sync products already have response data */}
                      {onGetReport && !isSync && isCompleted(txn) && (
                        <button
                          className="history-report-btn"
                          onClick={() => onGetReport(txn.transactionId, txn.productCode)}
                          title="Download report"
                        >
                          📄 Get Report
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="history-item-details">
                    <div><strong>Address:</strong> {txn.address}</div>
                    <div><strong>Product:</strong> {txn.productCode} / {txn.productType}</div>
                    <div><strong>Transaction ID:</strong> {txn.transactionId}</div>
                    {txn.confirmationNumber && (
                      <div><strong>Confirmation:</strong> {txn.confirmationNumber}</div>
                    )}
                    {txn.lastChecked && (
                      <div><strong>Last Checked:</strong> {new Date(txn.lastChecked).toLocaleString()}</div>
                    )}
                  </div>
                  {txn.statusResponse && !isSync && (
                    <details className="history-status-details">
                      <summary>View Latest Status</summary>
                      <pre>{formatJSON(txn.statusResponse)}</pre>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Request/Response Modal for Synchronous Transactions */}
      {selectedTransaction && (
        <div className="transaction-modal-overlay" onClick={() => setSelectedTransaction(null)}>
          <div className="transaction-modal" onClick={(e) => e.stopPropagation()}>
            <div className="transaction-modal-header">
              <h3>Transaction Details</h3>
              <button className="modal-close-btn" onClick={() => setSelectedTransaction(null)}>
                ✕
              </button>
            </div>
            <div className="transaction-modal-content">
              <div className="transaction-modal-section">
                <h4>Transaction Info</h4>
                <div className="transaction-info-grid">
                  <div><strong>Transaction ID:</strong> {selectedTransaction.transactionId}</div>
                  <div><strong>Status:</strong> {selectedTransaction.status}</div>
                  <div><strong>Address:</strong> {selectedTransaction.address}</div>
                  <div><strong>Product:</strong> {selectedTransaction.productCode} / {selectedTransaction.productType}</div>
                  <div><strong>Confirmation:</strong> {selectedTransaction.confirmationNumber || 'N/A'}</div>
                  <div><strong>Timestamp:</strong> {new Date(selectedTransaction.timestamp).toLocaleString()}</div>
                </div>
              </div>

              <div className="transaction-modal-section">
                <h4>Request Payload</h4>
                <pre className="transaction-modal-code">
                  {formatJSON(selectedTransaction.requestPayload)}
                </pre>
              </div>

              {selectedTransaction.responseData && (
                <div className="transaction-modal-section">
                  <h4>Response Data</h4>
                  <pre className="transaction-modal-code">
                    {formatJSON(selectedTransaction.responseData)}
                  </pre>
                </div>
              )}

              {selectedTransaction.statusResponse && (
                <div className="transaction-modal-section">
                  <h4>Status Response</h4>
                  <pre className="transaction-modal-code">
                    {formatJSON(selectedTransaction.statusResponse)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionHistory;
